import { DiscoveryFilterKey, PageBlockContentKind, PageBlockFilterMatch, PageBlockType, } from "@next-platform/packages-ts-types";
import { DiscoverySeeAllActionType } from "./discoverySeeAllAction";
const SEE_ALL_CONTENT_KINDS = new Set([PageBlockContentKind.Chatbot]);
// Tags is the only multi-valued facet. On any other one, "match all" with several values can never be satisfied, and the row
// endpoint rejects such a block as a configuration error, so its full listing is refused too.
const MULTI_VALUED_FILTER_KEYS = new Set([DiscoveryFilterKey.TAGS]);
const MIN_VALUES_FOR_UNSATISFIABLE_MATCH = 2;
const dedupeFilterValues = ([firstValue, ...otherValues]) => [
    firstValue,
    ...[...new Set(otherValues)].filter((value) => value !== firstValue),
];
// A copy, so a platform holding the action cannot reach back into the application settings.
const normalizeFilter = (filter) => ({
    filterKey: filter.filterKey,
    mode: filter.mode,
    values: dedupeFilterValues(filter.values),
    match: filter.match,
});
const isSatisfiableFilter = (filter) => filter.match !== PageBlockFilterMatch.All ||
    filter.values.length < MIN_VALUES_FOR_UNSATISFIABLE_MATCH ||
    MULTI_VALUED_FILTER_KEYS.has(filter.filterKey);
const resolveSearchAction = (block) => {
    const { contentKind, filters = [], sortPreset } = block.parameters;
    if (!SEE_ALL_CONTENT_KINDS.has(contentKind)) {
        return null;
    }
    const normalizedFilters = filters.map(normalizeFilter);
    if (!normalizedFilters.every(isSatisfiableFilter)) {
        return null;
    }
    return {
        type: DiscoverySeeAllActionType.SEARCH,
        blockKey: block.key,
        contentKind,
        filters: normalizedFilters,
        sortPreset: sortPreset ?? null,
    };
};
// The application settings carry every block whoever the viewer is, so the audience is checked here. A block that names no
// audiences is for everyone.
const isBlockForViewer = ({ block, viewerAudience }) => !block.audiences || block.audiences.includes(viewerAudience);
/**
 * The See all action of a block resolved for the Discovery page, or `null` when the row has no full listing or is not
 * meant for the viewer.
 */
export default function resolveDiscoverySeeAllActionForBlock({ block, viewerAudience, }) {
    if (!isBlockForViewer({ block, viewerAudience })) {
        return null;
    }
    switch (block.type) {
        case PageBlockType.Recommendation:
            return { type: DiscoverySeeAllActionType.RECOMMENDATIONS, blockKey: block.key };
        case PageBlockType.ContentRow:
            return resolveSearchAction(block);
        default:
            return null;
    }
}
